import type { ReactElement } from 'react';
import { List } from 'react-window';

import type {
  IVirtualAutocompleteListboxProperties,
  IVirtualSelectRowProperties,
} from '@features/autocompleteVirtualization/types';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  LIST_PADDING,
  VIRTUAL_AUTOCOMPLETE_GROUP_ROW_HEIGHT_PX,
  VIRTUAL_AUTOCOMPLETE_LISTBOX_VERTICAL_PADDING_SIDES,
  VIRTUAL_AUTOCOMPLETE_MAX_VISIBLE_OPTION_ROWS,
  VIRTUAL_AUTOCOMPLETE_OPTION_ROW_HEIGHT_SM_PX,
  VIRTUAL_AUTOCOMPLETE_OPTION_ROW_HEIGHT_XS_PX,
} from '@features/autocompleteVirtualization/constants';
import { getVirtualAutocompleteListElements } from '@features/autocompleteVirtualization/utils/getVirtualAutocompleteListElements';
import {
  getVirtualAutocompleteChildRowHeight,
  getVirtualAutocompleteListBodyHeightPx,
} from '@features/autocompleteVirtualization/utils/getVirtualAutocompleteListHeights';

export const VirtualAutocompleteListbox = (
  properties: Readonly<IVirtualAutocompleteListboxProperties>
): ReactElement => {
  const { ref, children, virtualRowComponent, ...other } = properties;
  const itemData = getVirtualAutocompleteListElements(children);

  const theme = useTheme();
  const matchesUpSm = useMediaQuery(theme.breakpoints.up('sm'));
  const itemCount = itemData.length;
  const optionRowHeightPx = matchesUpSm
    ? VIRTUAL_AUTOCOMPLETE_OPTION_ROW_HEIGHT_SM_PX
    : VIRTUAL_AUTOCOMPLETE_OPTION_ROW_HEIGHT_XS_PX;

  const listBodyHeightPx = getVirtualAutocompleteListBodyHeightPx({
    itemCount,
    items: itemData,
    optionRowHeightPx,
    groupRowHeightPx: VIRTUAL_AUTOCOMPLETE_GROUP_ROW_HEIGHT_PX,
    maxVisibleOptionRows: VIRTUAL_AUTOCOMPLETE_MAX_VISIBLE_OPTION_ROWS,
  });

  const resolveRowHeight = (
    index: number,
    rowProperties: IVirtualSelectRowProperties
  ): number =>
    getVirtualAutocompleteChildRowHeight({
      optionRowHeightPx,
      child: rowProperties.items[index],
      groupRowHeightPx: VIRTUAL_AUTOCOMPLETE_GROUP_ROW_HEIGHT_PX,
    });

  return (
    <Box ref={ref}>
      <List<IVirtualSelectRowProperties, 'ul'>
        {...other}
        style={{
          width: '100%',
          height:
            listBodyHeightPx +
            VIRTUAL_AUTOCOMPLETE_LISTBOX_VERTICAL_PADDING_SIDES * LIST_PADDING,
        }}
        overscanCount={5}
        rowComponent={virtualRowComponent}
        rowCount={itemCount}
        rowHeight={resolveRowHeight}
        rowProps={{ items: itemData }}
        tagName="ul"
      />
    </Box>
  );
};
